import React, { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

const VARS = "{{customer_first_name}} {{salon_name}} {{service}} {{employee}} {{date}} {{time}} {{price}} {{expires}} {{booking_link}}";

export function WaitlistSettingsTab({ t }) {
    const { data } = useQuery({ queryKey: ["waitlist-settings"], queryFn: async () => (await api.get("/waitlist/settings")).data });
    const [f, setF] = useState(null);
    const [saving, setSaving] = useState(false);
    useEffect(() => { if (data && !f) setF({ ...data, templates: data.templates || {} }); }, [data, f]);
    if (!f) return <Loader2 className="h-6 w-6 animate-spin text-accent" />;
    const set = (patch) => setF((x) => ({ ...x, ...patch }));
    const setTpl = (kind, k, v) => setF((x) => ({ ...x, templates: { ...x.templates, [kind]: { ...(x.templates[kind] || {}), [k]: v } } }));
    const save = async () => {
        setSaving(true);
        try { await api.put("/waitlist/settings", f); toast.success(t("common.saved")); } catch (e) { toast.error(formatApiError(e)); } finally { setSaving(false); }
    };
    const Row = ({ k, label }) => <label className="flex items-center justify-between py-2"><span className="text-sm">{label}</span><Switch checked={!!f[k]} onCheckedChange={(v) => set({ [k]: v })} data-testid={`wls-${k}`} /></label>;
    return (
        <div className="max-w-2xl space-y-6" data-testid="waitlist-settings">
            <div className="rounded-2xl border border-border/70 bg-card p-6">
                <h3 className="mb-2 font-semibold">{t("wl.settings.title")}</h3>
                <p className="mb-4 text-sm text-muted-foreground">{t("wl.subtitle")}</p>
                <div className="divide-y divide-border/60">
                    <Row k="enabled" label={t("wl.settings.enabled")} />
                    <Row k="auto_match" label={t("wl.settings.autoMatch")} />
                    <Row k="auto_offer" label={t("wl.settings.autoOffer")} />
                    <Row k="auto_continue" label={t("wl.settings.autoContinue")} />
                    <Row k="notify_email" label={t("wl.settings.notifyEmail")} />
                    <Row k="paused" label={t("wl.settings.paused")} />
                </div>
                <div className="mt-4 grid gap-4 sm:grid-cols-3">
                    <div><Label>{t("wl.settings.minScore")} (%)</Label><Input type="number" min={0} max={100} value={f.min_score} onChange={(e) => set({ min_score: Number(e.target.value) })} className="mt-1.5" data-testid="wls-min-score" /></div>
                    <div><Label>{t("wl.settings.strategy")}</Label>
                        <select value={f.strategy} onChange={(e) => set({ strategy: e.target.value })} className="mt-1.5 h-10 w-full rounded-md border border-input bg-background px-3 text-sm" data-testid="wls-strategy">
                            {["one", "top3", "all"].map((k) => <option key={k} value={k}>{t(`wl.settings.${k}`)}</option>)}
                        </select></div>
                    <div><Label>{t("wl.settings.hold")}</Label>
                        <select value={f.hold_minutes} onChange={(e) => set({ hold_minutes: Number(e.target.value) })} className="mt-1.5 h-10 w-full rounded-md border border-input bg-background px-3 text-sm" data-testid="wls-hold">
                            {[5, 10, 15, 30, 60].map((m) => <option key={m} value={m}>{m} {t("wl.settings.minutes")}</option>)}
                        </select></div>
                </div>
            </div>
            <div className="rounded-2xl border border-border/70 bg-card p-6">
                <h3 className="mb-1 font-semibold">{t("wl.settings.templates")}</h3>
                <p className="mb-4 text-xs text-muted-foreground">{t("wl.settings.variables")}: <code className="rounded bg-secondary px-1">{VARS}</code></p>
                {[["offer", "tplOffer"], ["confirmed", "tplConfirmed"], ["expired", "tplExpired"]].map(([kind, l]) => (
                    <div key={kind} className="mb-4 space-y-2 rounded-xl border border-border/60 p-3">
                        <p className="text-sm font-semibold">{t(`wl.settings.${l}`)}</p>
                        <Input placeholder={t("wl.settings.subject")} value={f.templates[kind]?.subject || ""} onChange={(e) => setTpl(kind, "subject", e.target.value)} data-testid={`wls-tpl-${kind}-subject`} />
                        <Textarea rows={4} placeholder={t("wl.settings.message")} value={f.templates[kind]?.message || ""} onChange={(e) => setTpl(kind, "message", e.target.value)} data-testid={`wls-tpl-${kind}-message`} />
                    </div>
                ))}
                <div><Label>{t("wl.settings.signature")}</Label><Input value={f.templates.signature || ""} onChange={(e) => setF((x) => ({ ...x, templates: { ...x.templates, signature: e.target.value } }))} className="mt-1.5" data-testid="wls-signature" /></div>
            </div>
            <Button onClick={save} disabled={saving} className="rounded-full" data-testid="wls-save">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : t("common.save")}</Button>
        </div>
    );
}
